// Abgleich-Panel: Ampel-Zusammenfassung des Bestellabgleichs (compareService, s. api.getCompare/
// getCompareAmpl) + optional die HTN/AMPL-Pruefung (Bauplan Punkt 3b + 4-HTN-Ergaenzung). Ersetzt
// die vorherige einfache Chip-Kachel durch eine sprechende Ampel-Meldung (Vorbild OC-Pilot
// DocumentDetail.tsx notices-Block). Drei Zustaende: laedt / kein SAP-Treffer (amber) / geprueft (gruen).
// compare === null bedeutet "Abgleich fehlgeschlagen" (getCompare() im Aufrufer catch->null) —
// dediziert von "noch am Laden" unterschieden.

import React from 'react';
import { CheckCircle2, AlertTriangle, Loader2, ShieldCheck } from 'lucide-react';
import { Card, SectionTitle, btnGhost } from '../ui';

// HINWEIS: anders als das OC-Pilot-Vorbild hat der AB-Modul-compare-Endpoint kein globales
// "amplEnabled"-Flag — der "HTN pruefen"-Button wird daher IMMER angeboten (RULES: Buttons nie
// verstecken, nur sauber degradieren); liefert der Adapter keine AMPL-Daten, bleiben die
// mfrpnState-Werte je Zeile einfach leer/'no_ampl' und die HTN-Badges/HtnNotice unauffaellig.
const AbCompareStatusPanel = ({
  isDarkMode, t, compareLoading, compare, docLineCount,
  htnBusy, htnChecked, onRunHtnCheck,
}) => {
  if (docLineCount === 0) return null;

  if (compareLoading) {
    return (
      <Card isDarkMode={isDarkMode}>
        <p className={`text-sm flex items-center gap-2 ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
          <Loader2 className="w-4 h-4 animate-spin" />{t('abPdf.compareLoading')}
        </p>
      </Card>
    );
  }

  const lines = Array.isArray(compare?.lines) ? compare.lines : null;

  if (!lines) {
    return (
      <Card isDarkMode={isDarkMode} className={isDarkMode ? 'border-amber-400/30' : 'border-amber-200'}>
        <SectionTitle isDarkMode={isDarkMode}>
          <span className={`flex items-center gap-2 ${isDarkMode ? 'text-amber-300' : 'text-amber-600'}`}>
            <AlertTriangle className="w-4 h-4" />{t('abPdf.compareUnavailableTitle')}
          </span>
        </SectionTitle>
        <p className={`text-sm ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>{t('abPdf.compareUnavailableMsg')}</p>
      </Card>
    );
  }

  const matched = lines.filter((l) => l.state !== 'not_found').length;
  const notFound = lines.length - matched;
  const diffs = lines.reduce((n, l) => n + Object.values(l.fields || {}).filter((f) => f.state === 'diff').length, 0);
  const orderNos = [...new Set(lines.map((l) => l.ebeln).filter(Boolean))];

  const htnMismatch = lines.filter((l) => l.mfrpnState === 'mismatch').length;
  const htnMissing = lines.filter((l) => l.mfrpnState === 'missing_htn').length;

  const htnButton = (
    <button type="button" disabled={htnBusy || docLineCount === 0} onClick={onRunHtnCheck} className={btnGhost(isDarkMode)}>
      <span className="flex items-center gap-2">
        {htnBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
        {htnBusy ? t('abPdf.htnChecking') : t('abPdf.htnCheckButton')}
      </span>
    </button>
  );

  if (matched === 0) {
    return (
      <div className="space-y-3">
        <Card isDarkMode={isDarkMode} className={isDarkMode ? 'border-amber-400/30' : 'border-amber-200'}>
          <SectionTitle isDarkMode={isDarkMode} right={htnButton}>
            <span className={`flex items-center gap-2 ${isDarkMode ? 'text-amber-300' : 'text-amber-600'}`}>
              <AlertTriangle className="w-4 h-4" />{t('abPdf.compareNoneFoundTitle')}
            </span>
          </SectionTitle>
          <p className={`text-sm ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>
            {t('abPdf.compareNoneFoundMsg', { orders: orderNos.length ? orderNos.join(', ') : '—' })}
          </p>
        </Card>
        {htnChecked && !htnBusy && (
          <HtnNotice isDarkMode={isDarkMode} t={t} htnMismatch={htnMismatch} htnMissing={htnMissing} />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Card isDarkMode={isDarkMode} className={isDarkMode ? 'border-emerald-400/30' : 'border-emerald-200'}>
        <SectionTitle isDarkMode={isDarkMode} right={htnButton}>
          <span className={`flex items-center gap-2 ${isDarkMode ? 'text-emerald-300' : 'text-emerald-600'}`}>
            <CheckCircle2 className="w-4 h-4" />{t('abPdf.compareCheckedTitle')}
          </span>
        </SectionTitle>
        <p className={`text-sm ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>
          {t('abPdf.compareCheckedMsg', { matched, total: lines.length })}
          {diffs > 0 ? t('abPdf.compareDiffSuffix', { n: diffs }) : ''}
          {notFound > 0 ? t('abPdf.compareNotFoundSuffix', { n: notFound }) : ''}
        </p>
      </Card>
      {htnChecked && !htnBusy && (
        <HtnNotice isDarkMode={isDarkMode} t={t} htnMismatch={htnMismatch} htnMissing={htnMissing} />
      )}
    </div>
  );
};

const HtnNotice = ({ isDarkMode, t, htnMismatch, htnMissing }) => {
  const ok = htnMismatch === 0;
  return (
    <Card isDarkMode={isDarkMode} className={ok ? (isDarkMode ? 'border-emerald-400/30' : 'border-emerald-200') : (isDarkMode ? 'border-amber-400/30' : 'border-amber-200')}>
      <SectionTitle isDarkMode={isDarkMode}>
        <span className={`flex items-center gap-2 ${ok ? (isDarkMode ? 'text-emerald-300' : 'text-emerald-600') : (isDarkMode ? 'text-amber-300' : 'text-amber-600')}`}>
          {ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}{t('abPdf.htnResultTitle')}
        </span>
      </SectionTitle>
      <p className={`text-sm ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>
        {ok
          ? (htnMissing > 0 ? t('abPdf.htnResultOkWithMissing', { n: htnMissing }) : t('abPdf.htnResultOkClean'))
          : t('abPdf.htnResultProblemBase', { mismatch: htnMismatch }) + (htnMissing > 0 ? t('abPdf.htnResultProblemMissingSuffix', { missing: htnMissing }) : '')}
      </p>
    </Card>
  );
};

export default AbCompareStatusPanel;
